// src/components/Footer.jsx
import React, { useState } from 'react';
import { ExternalLink, FileText, MessageCircle, Twitter, Shield, Coffee } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  // ✅ endpoint fixo em produção
  const WHATSAPP_ENDPOINT = 'https://cofy.app/api/whatsapp_subscribe.php';

  const LINKS = {
    WHITEPAPER: 'https://cofy.app/COFY_Whitepaper.pdf',
    BSCSCAN:
      'https://bscscan.com/token/0xd6210bE2B4d3EdEdD55c9FFB1aE833aEa94811f4', // ✅ contrato token mainnet
    DOCS: 'https://cofy.app/api.html',
    TELEGRAM: 'https://t.me/cofy',
    DISCORD: 'https://discord.gg/yourInvite',
    TWITTER: 'https://twitter.com/cofytoken',
    COMO_COMPRAR: '#como-comprar',
    SIMULADOR: '#invest',     // anchor existe (sr-only) para compatibilidade
    BENEFICIOS: '#benefits',  // ✅ bate com a seção real
    PRIVACY: '/privacy',
    TERMS: '/terms',
  };

  const footerLinks = [
    {
      title: 'Recursos',
      links: [
        { name: 'Whitepaper', href: LINKS.WHITEPAPER, icon: FileText, external: true },
        { name: 'BscScan (Contrato)', href: LINKS.BSCSCAN, icon: Shield, external: true },
        { name: 'Documentação', href: LINKS.DOCS, icon: Coffee, external: true },
      ],
    },
    {
      title: 'Comunidade',
      links: [
        { name: 'Telegram', href: LINKS.TELEGRAM, icon: MessageCircle, external: true },
        { name: 'Discord', href: LINKS.DISCORD, icon: MessageCircle, external: true },
        { name: 'Twitter/X', href: LINKS.TWITTER, icon: Twitter, external: true },
      ],
    },
    {
      title: 'Investimento',
      links: [
        { name: 'Como Comprar', href: LINKS.COMO_COMPRAR, icon: null, external: false },
        { name: 'Simulador', href: LINKS.SIMULADOR, icon: null, external: false },
        { name: 'Benefícios', href: LINKS.BENEFICIOS, icon: null, external: false },
      ],
    },
  ];

  const isExternal = (href, forceExternal) =>
    forceExternal || /^https?:\/\//i.test(href);

  // ===== Newsletter via WhatsApp =====
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [ok, setOk] = useState(false);
  const [consent, setConsent] = useState(true); // marcar por padrão (ajuste conforme LGPD)

  // Utils
  const onlyDigits = (s) => (s || '').replace(/\D+/g, '');

  // Máscara BR: (DD) 9 9999-9999 | (DD) 9999-9999
  const maskBR = (v) => {
    const d = onlyDigits(v).slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 3)} ${d.slice(3, 7)}-${d.slice(7, 11)}`;
  };

  // Converte para E.164 com tolerância:
  // - aceita "+5511999999999"
  // - aceita "5511999999999"
  // - aceita local "11999999999" (prefixa +55)
  const toE164 = (v) => {
    const trimmed = (v || '').trim();
    if (trimmed.startsWith('+')) {
      return /^\+\d{10,15}$/.test(trimmed) ? trimmed : null;
    }
    const d = onlyDigits(trimmed);
    if (!d) return null;
    if (d.startsWith('55') && d.length >= 12 && d.length <= 13) {
      const candidate = `+${d}`;
      return /^\+\d{10,15}$/.test(candidate) ? candidate : null;
    }
    if (d.length >= 10 && d.length <= 11) {
      const candidate = `+55${d}`;
      return /^\+\d{10,15}$/.test(candidate) ? candidate : null;
    }
    return null;
  };

  const handleWhatsappSubscribe = async (e) => {
    e.preventDefault();
    setMsg('');
    setOk(false);

    if (!consent) {
      setMsg('É necessário consentir para receber novidades via WhatsApp.');
      return;
    }

    const e164 = toE164(phone);
    if (!e164) {
      setMsg('Informe um WhatsApp válido (ex.: (11) 9 9876-5432).');
      return;
    }

    try {
      setLoading(true);
      const r = await fetch(WHATSAPP_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ phone: e164, consent: true }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) {
        throw new Error(j?.error || `Falha HTTP ${r.status}`);
      }
      setOk(true);
      setMsg('Inscrição realizada com sucesso! Você receberá novidades no seu WhatsApp.');
      setPhone('');
    } catch (err) {
      setMsg(err?.message || 'Não foi possível concluir agora. Tente novamente em alguns minutos.');
    } finally {
      setLoading(false);
    }
  };

  const maskedPhone = maskBR(phone);
  const isInvalid = phone && !toE164(phone);

  return (
    <footer className="bg-background-darker border-t border-white/10">
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand section */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center">
                <Coffee className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-2xl font-black text-primary-text">COFY Token</span>
                <div className="text-sm text-muted-text">Digital Coffee Market</div>
              </div>
            </div>

            <p className="text-muted-text leading-relaxed mb-6">
              Revolucionando o mercado do café através da tecnologia blockchain.
              O futuro do agronegócio digital está aqui.
            </p>

            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 text-sm font-medium">Sistema Operacional</span>
            </div>
          </div>

          {/* Links sections */}
          {footerLinks.map((section, index) => (
            <div key={index}>
              <h3 className="text-primary-text font-bold mb-6 text-lg">{section.title}</h3>
              <ul className="space-y-4">
                {section.links.map((link, linkIndex) => {
                  const Icon = link.icon;
                  const external = isExternal(link.href, link.external);
                  return (
                    <li key={linkIndex}>
                      <a
                        href={link.href}
                        {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
                        className="flex items-center space-x-3 text-muted-text hover:text-green-400 transition-colors group"
                      >
                        {Icon && <Icon className="w-4 h-4 group-hover:scale-110 transition-transform" />}
                        <span>{link.name}</span>
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter via WhatsApp */}
        <div className="bg-card-dark/50 backdrop-blur-sm rounded-2xl p-8 mb-12 border border-white/10">
          <div className="text-center md:text-left md:flex md:items-center md:justify-between">
            <div className="mb-6 md:mb-0">
              <h3 className="text-primary-text font-bold text-xl mb-2">
                Receba novidades no seu WhatsApp
              </h3>
              <p className="text-muted-text">
                Atualizações sobre o COFY Token, mercado do café e oportunidades.
              </p>
            </div>

            <form
              onSubmit={handleWhatsappSubscribe}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto md:mx-0 w-full"
            >
              <input
                type="tel"
                inputMode="tel"
                placeholder="Seu WhatsApp (ex.: (11) 9 9876-5432)"
                value={maskedPhone}
                onChange={(e) => setPhone(e.target.value)}
                className={`flex-1 bg-card-dark border rounded-xl px-4 py-3 text-primary-text placeholder-muted-text focus:outline-none transition-colors
                  ${isInvalid ? 'border-red-400 focus:border-red-400' : 'border-white/20 focus:border-green-400'}`}
                aria-label="Número do WhatsApp"
                aria-invalid={isInvalid ? 'true' : 'false'}
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-500 hover:to-blue-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 whitespace-nowrap"
              >
                {loading ? 'Enviando…' : 'Quero receber'}
              </button>
            </form>
          </div>

          <div className="mt-3 flex items-start gap-2 text-xs text-muted-text">
            <input
              id="consent"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5"
            />
            <label htmlFor="consent">
              Concordo em receber comunicações do COFY via WhatsApp. Você pode cancelar a qualquer momento.
            </label>
          </div>

          {msg && (
            <div
              className={`mt-3 text-sm ${ok ? 'text-green-400' : 'text-yellow-300'}`}
              role="status"
              aria-live="polite"
            >
              {msg}
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="bg-card-dark/30 rounded-2xl p-6 mb-8 border border-white/5">
          <p className="text-muted-text text-sm leading-relaxed text-center">
            <strong className="text-primary-text">Aviso Legal:</strong> O COFY Token é um ativo digital em desenvolvimento.
            Investimentos em criptomoedas envolvem riscos significativos. Esta página é apenas informativa.
            Consulte um consultor financeiro qualificado antes de investir.
          </p>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-muted-text text-sm mb-4 md:mb-0">
              © {currentYear} COFY Token. Todos os direitos reservados.
            </div>

            <div className="flex items-center space-x-6">
              <a href={LINKS.PRIVACY} className="text-muted-text hover:text-green-400 text-sm transition-colors">
                Política de Privacidade
              </a>
              <a href={LINKS.TERMS} className="text-muted-text hover:text-green-400 text-sm transition-colors">
                Termos de Uso
              </a>
              <div className="flex items-center space-x-2">
                <Coffee className="w-4 h-4 text-yellow-600" />
                <span className="text-muted-text text-sm">Feito com café brasileiro ☕</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
